import type {DataViewerFilterClearConfig} from "~/components/ottopilotDataViewer/dataViewerFilterClearConfig";
import {
    DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
    isCreatedAtDateRangeFilterLineItem,
    isDefaultCreatedAtDateRangeFilters,
    mergeDefaultCreatedAtMonthFilter,
    shouldApplyDefaultCreatedAtMonthFilter,
} from "~/components/ottopilotDataViewer/dataViewerDateRangeDefaults";
import type {FilterConfiguration, FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {filterLineItemsEqual, isPreservedFilterLineItem} from "~/components/ottopilotDataViewer/filterLineItemEquality";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

/** Shared filter clearing is independent of workspace page data or row actions. */
export type DataViewerFilterController<T extends {id: Uuid}> = Pick<OttopilotDataViewerController<T>,
    "filterConfiguration" | "searchConfiguration" | "filterClearConfig" | "defaultCreatedAtMonthFilter" | "dateRangeColumnId" | "setFilterConfiguration" | "setSearchConfiguration"
>;

function getPreservedColumnIdSet(config: DataViewerFilterClearConfig | undefined): Set<string> {
    if (config?.preservedFilterLineItemColumnIds === undefined) {
        return new Set();
    }
    return new Set(config.preservedFilterLineItemColumnIds);
}

function getPreservedLineItems(config: DataViewerFilterClearConfig | undefined): ReadonlyArray<FilterConfigurationLineItem> {
    return config?.preservedFilterLineItems ?? [];
}

export function isPreservedFilterConfigurationLineItem(
    lineItem: FilterConfigurationLineItem,
    config: DataViewerFilterClearConfig | undefined,
): boolean {
    const preservedColumnIds = getPreservedColumnIdSet(config);
    if (preservedColumnIds.has(lineItem.columnId) === true) {
        return true;
    }
    return isPreservedFilterLineItem(lineItem, getPreservedLineItems(config));
}

export function getClearableFilterLineItems(
    filterConfiguration: FilterConfiguration,
    config: DataViewerFilterClearConfig | undefined,
    options?: {dateRangeColumnId?: string; excludeDefaultCreatedAtDateRange?: boolean},
): FilterConfiguration["lineItems"] {
    let lineItems = filterConfiguration.lineItems.filter(
        (lineItem) => isPreservedFilterConfigurationLineItem(lineItem, config) === false,
    );

    if (
        options?.excludeDefaultCreatedAtDateRange !== false &&
        isDefaultCreatedAtDateRangeFilters(filterConfiguration.lineItems, options?.dateRangeColumnId)
    ) {
        lineItems = lineItems.filter((lineItem) => isCreatedAtDateRangeFilterLineItem(lineItem, options?.dateRangeColumnId) === false);
    }

    return lineItems;
}

export function buildDataViewerFilterClearContext<T extends {id: Uuid}>(
    controller: DataViewerFilterController<T>,
): {
    filterConfiguration: FilterConfiguration;
    searchConfiguration: typeof controller.searchConfiguration;
    clearableFilterLineItems: FilterConfiguration["lineItems"];
} {
    const clearableFilterLineItems = getClearableFilterLineItems(
        controller.filterConfiguration,
        controller.filterClearConfig,
        {
            dateRangeColumnId: controller.dateRangeColumnId,
            excludeDefaultCreatedAtDateRange: shouldApplyDefaultCreatedAtMonthFilter(
                controller.defaultCreatedAtMonthFilter,
            ),
        },
    );
    return {
        filterConfiguration: controller.filterConfiguration,
        searchConfiguration: controller.searchConfiguration,
        clearableFilterLineItems,
    };
}

export function hasActiveDataViewerFilters<T extends {id: Uuid}>(
    controller: DataViewerFilterController<T>,
): boolean {
    const context = buildDataViewerFilterClearContext(controller);
    const config = controller.filterClearConfig;

    if (config?.hasClearableFilters !== undefined) {
        return config.hasClearableFilters(context);
    }

    const hasClearableLineItems = context.clearableFilterLineItems.length > 0;
    const hasSearch =
        config?.clearSearch === false
            ? false
            : context.searchConfiguration.searchValue.trim() !== "";

    return hasClearableLineItems || hasSearch;
}

export function clearAllDataViewerFilters<T extends {id: Uuid}>(
    controller: DataViewerFilterController<T>,
): void {
    const context = buildDataViewerFilterClearContext(controller);
    const config = controller.filterClearConfig;

    const applyDefaultClear = () => {
        const keptLineItems = controller.filterConfiguration.lineItems.filter((lineItem) =>
            isPreservedFilterConfigurationLineItem(lineItem, config),
        );
        const restoredLineItems = getPreservedLineItems(config).filter((preservedLineItem) => {
            return keptLineItems.some((lineItem) => filterLineItemsEqual(lineItem, preservedLineItem) === true) === false;
        });
        const preservedLineItems = [...keptLineItems, ...restoredLineItems];

        const applyDefaultDateRange = shouldApplyDefaultCreatedAtMonthFilter(
            controller.defaultCreatedAtMonthFilter,
        );
        const nextFilterConfiguration = applyDefaultDateRange
            ? mergeDefaultCreatedAtMonthFilter(
                  {
                      revisionId: generateUuid(),
                      lineItems: preservedLineItems,
                  },
                  controller.dateRangeColumnId ?? DATA_VIEWER_CREATED_AT_DATE_RANGE_COLUMN_ID,
                  true,
              )
            : {
                  revisionId: generateUuid(),
                  lineItems: preservedLineItems,
              };

        controller.setFilterConfiguration(nextFilterConfiguration);

        if (config?.clearSearch !== false) {
            controller.setSearchConfiguration((prev) => ({
                ...prev,
                revisionId: generateUuid(),
                searchValue: "",
            }));
        }
    };

    if (config?.onClearAllFilters !== undefined) {
        config.onClearAllFilters(context, applyDefaultClear);
        return;
    }

    applyDefaultClear();
}
